import { createAsyncThunk } from "@reduxjs/toolkit";

import { alertActions } from "../alert/alertSlice";

import {
    createField as createFieldFromAPI,
    getAgeLevels as getAgeLevelsFromAPI,
    getBracketTypes as getBracketTypesFromAPI,
    getDesignations as getDesignationsFromAPI,
    getFieldsList,
    getNotice as getNoticeFromAPI,
    getPhotos as getPhotosFromAPI,
    getPositions as getPositionsFromAPI,
    getSeasons as getSeasonsFromAPI,
    getSiteSetting,
    getSites as getSitesFromAPI,
    getUmpires as getUmpiresFromAPI,
    getUserLevels as getUserLevelsFromAPI,
    updateField as updateFieldFromAPI,
    updateNotice as updateNoticeFromAPI,
} from "../../lib/api";
import {
    IAPIResponse,
    IAgeLevel,
    IDesignation,
    IField,
    INotice,
    IPhoto,
    IPlayoffBracket,
    IPosition,
    ISeason,
    ISite,
    ISiteSetting,
    IUser,
    IUserLevel,
} from "../../types/api";
import { ILeagueMgmtFieldData, ISiteNoticeForm } from "../../types/forms";

interface IGetAgeLevelsResponse extends IAPIResponse {
    ageLevels: IAgeLevel[];
}

export const getAgeLevels = createAsyncThunk<
    IGetAgeLevelsResponse,
    void,
    { rejectValue: string }
>("global/getAgeLevels", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getAgeLevelsFromAPI();

        return response;
    } catch (e) {
        const error = e as string;
        // dispatch(
        //     alertActions.error({
        //         level: "danger",
        //         text: "Age Level retrieval failed. Please try again later or contact an admin",
        //         title: error,
        //     }),
        // );
        return rejectWithValue(error);
    }
});

export const getUmpires = createAsyncThunk<
    Array<Omit<IUser, "password" | "meta"> & { name: string }>,
    void,
    { rejectValue: string }
>("global/getUmpires", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getUmpiresFromAPI();

        return response.umpires;
    } catch (e) {
        const error = e as string;
        // dispatch(
        //     alertActions.error({
        //         level: "danger",
        //         text: "Umpire retrieval failed. Please try again later or contact an admin",
        //         title: error,
        //     }),
        // );
        return rejectWithValue(error);
    }
});

export const getSettings = createAsyncThunk<
    ISiteSetting[],
    void,
    { rejectValue: string }
>("global/getSettings", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getSiteSetting();

        const settings = Array.isArray(response.setting)
            ? response.setting
            : [response.setting];

        return settings;
    } catch (e) {
        const error = e as string;
        // dispatch(
        //     alertActions.error({
        //         level: "danger",
        //         text: "Settings retrieval failed. Please try again later or contact an admin",
        //         title: error,
        //     }),
        // );
        return rejectWithValue(error);
    }
});

export const getFields = createAsyncThunk<
    IField[],
    void,
    { rejectValue: string }
>("global/getFields", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getFieldsList();

        return response.fields;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const createField = createAsyncThunk<
    IField[],
    ILeagueMgmtFieldData,
    { rejectValue: string }
>("global/createField", async (field, { rejectWithValue, dispatch }) => {
    try {
        const response = await createFieldFromAPI(field);

        dispatch(
            alertActions.success({
                title: "Field created successfully",
                dismissible: true,
            }),
        );

        return response.fields;
    } catch (e) {
        const error = e as string;

        dispatch(
            alertActions.error({
                title: "The server encountered a problem creating the field.",
                text: "If the problem continues, please reach out to an admin.",
                dismissible: true,
            }),
        );
        return rejectWithValue(error);
    }
});

export const updateField = createAsyncThunk<
    IField[],
    { fieldData: ILeagueMgmtFieldData; fieldId: IField["id"] },
    { rejectValue: string }
>(
    "global/updateField",
    async ({ fieldData, fieldId }, { rejectWithValue, dispatch }) => {
        try {
            const response = await updateFieldFromAPI(fieldData, fieldId);

            dispatch(
                alertActions.success({
                    title: "Field updated successfully",
                    dismissible: true,
                }),
            );

            return response.fields;
        } catch (e) {
            const error = e as string;

            dispatch(
                alertActions.error({
                    title: "The server encountered a problem updating the field.",
                    text: "If the problem continues, please reach out to an admin.",
                    dismissible: true,
                }),
            );
            return rejectWithValue(error);
        }
    },
);

export const getSeasons = createAsyncThunk<
    ISeason[],
    void,
    { rejectValue: string }
>("global/getSeasons", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getSeasonsFromAPI();

        return response.seasons;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getNotice = createAsyncThunk<
    INotice | false,
    void,
    { rejectValue: string }
>("global/getNotice", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getNoticeFromAPI();

        return response.notice;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const updateNotice = createAsyncThunk<
    INotice | false,
    ISiteNoticeForm,
    { rejectValue: string }
>("global/updateNotice", async (notice, { rejectWithValue, dispatch }) => {
    try {
        const response = await updateNoticeFromAPI(notice);

        dispatch(
            alertActions.success({
                title: "Site notice updated successfully",
                dismissible: true,
            }),
        );

        return response.notice;
    } catch (e) {
        const error = e as string;

        dispatch(
            alertActions.error({
                title: "The server encountered a problem updating the site notice.",
                text: "If the problem continues, please reach out to an admin.",
                dismissible: true,
            }),
        );
        return rejectWithValue(error);
    }
});

export const getPhotos = createAsyncThunk<
    IPhoto[],
    void,
    { rejectValue: string }
>("global/getPhotos", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getPhotosFromAPI();

        return response.photos;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getDesignations = createAsyncThunk<
    IDesignation[],
    void,
    { rejectValue: string }
>("global/getDesignations", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getDesignationsFromAPI();

        return response.designations;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getBracketTypes = createAsyncThunk<
    IPlayoffBracket[],
    void,
    { rejectValue: string }
>("global/getBracketTypes", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getBracketTypesFromAPI();

        return response.brackets;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getPositions = createAsyncThunk<
    IPosition[],
    void,
    { rejectValue: string }
>("global/getPositions", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getPositionsFromAPI();

        return response.positions;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getSites = createAsyncThunk<
    ISite[],
    void,
    { rejectValue: string }
>("global/getSite", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getSitesFromAPI();

        return response.sites;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getUserLevels = createAsyncThunk<
    IUserLevel[],
    void,
    { rejectValue: string }
>("global/getUserLevels", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getUserLevelsFromAPI();

        return response.userLevels;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});
